/**
 * Svelte store for managing households and members.
 * Consolidates former householdStore (single) + householdsStore (multi).
 */
import { writable, get } from 'svelte/store';
import { api, type Household, type HouseholdMember, type HouseholdType } from '../api';

/**
 * Separate store for the default household with members (legacy single-household API).
 * Used by Settings page for member CRUD.
 */
const defaultHousehold = writable<Household | null>(null);
let defaultHouseholdLoaded = false;

function createHouseholdsStore() {
  const { subscribe, set, update } = writable<Household[]>([]);

  let loaded = false;

  return {
    subscribe,

    get isLoaded() {
      return loaded;
    },

    async load(): Promise<void> {
      try {
        const households = await api.households.getHouseholds();
        set(households);
        loaded = true;
      } catch (error) {
        console.error('Failed to load households:', error);
        throw error;
      }
    },

    async create(name: string, type: HouseholdType): Promise<Household> {
      try {
        const household = await api.households.createHousehold(name, type);
        update((list) => [...list, household]);
        return household;
      } catch (error) {
        console.error('Failed to create household:', error);
        throw error;
      }
    },

    async update(id: string, data: { name?: string; type?: HouseholdType }): Promise<void> {
      try {
        await api.households.updateHousehold(id, data);
        update((list) =>
          list.map((h) =>
            h.id === id ? { ...h, ...data, updatedAt: new Date().toISOString() } : h,
          ),
        );
      } catch (error) {
        console.error('Failed to update household:', error);
        throw error;
      }
    },

    async delete(id: string): Promise<void> {
      try {
        await api.households.deleteHousehold(id);
        update((list) => list.filter((h) => h.id !== id));
      } catch (error) {
        console.error('Failed to delete household:', error);
        throw error;
      }
    },

    getAll(): Household[] {
      return get({ subscribe });
    },

    // --- Default household with members (legacy single-household API) ---

    /** Subscribe to the default household (with members) */
    get defaultHousehold() {
      return { subscribe: defaultHousehold.subscribe };
    },

    get isDefaultHouseholdLoaded() {
      return defaultHouseholdLoaded;
    },

    async loadDefaultHousehold(): Promise<void> {
      try {
        const household = await api.households.getHousehold();
        defaultHousehold.set(household);
        defaultHouseholdLoaded = true;
      } catch (error) {
        console.error('Failed to load default household:', error);
        throw error;
      }
    },

    async addMember(name: string, role?: string): Promise<HouseholdMember> {
      try {
        const member = await api.households.addHouseholdMember(name, role);
        defaultHousehold.update((h) => {
          if (!h) return h;
          return { ...h, members: [...h.members, member], updatedAt: new Date().toISOString() };
        });
        return member;
      } catch (error) {
        console.error('Failed to add household member:', error);
        throw error;
      }
    },

    async updateMember(id: string, name: string, role?: string): Promise<void> {
      try {
        await api.households.updateHouseholdMember(id, name, role);
        defaultHousehold.update((h) => {
          if (!h) return h;
          return {
            ...h,
            members: h.members.map((m) => (m.id === id ? { ...m, name, role } : m)),
            updatedAt: new Date().toISOString(),
          };
        });
      } catch (error) {
        console.error('Failed to update household member:', error);
        throw error;
      }
    },

    async removeMember(id: string): Promise<void> {
      try {
        await api.households.removeHouseholdMember(id);
        defaultHousehold.update((h) => {
          if (!h) return h;
          return {
            ...h,
            members: h.members.filter((m) => m.id !== id),
            updatedAt: new Date().toISOString(),
          };
        });
      } catch (error) {
        console.error('Failed to remove household member:', error);
        throw error;
      }
    },

    getMembers(): HouseholdMember[] {
      const h = get(defaultHousehold);
      return h?.members ?? [];
    },

    reset(): void {
      set([]);
      loaded = false;
      defaultHousehold.set(null);
      defaultHouseholdLoaded = false;
    },
  };
}

export const householdsStore = createHouseholdsStore();
